import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2400&auto=format&fit=crop";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);
  const opacity = useTransform(scrollY, [0, 700], [1, 0.2]);

  return (
    <section id="home" data-testid="hero-section" className="relative h-screen overflow-hidden bg-ink">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <img
          src={HERO_IMG}
          alt="Refined fine dining table setting"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/40 to-ink/90" />

      <motion.div
        style={{ opacity }}
        className="relative z-10 h-full max-w-[1600px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-24 lg:pb-28"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="eyebrow mb-6"
          data-testid="hero-eyebrow"
        >
          BR Hospitality — Recruitment &amp; Advisory
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-serif text-ivory text-5xl sm:text-6xl lg:text-8xl leading-[1.02] font-light max-w-5xl"
        >
          <MaskedLine delay={0.55}>Exceptional Hospitality</MaskedLine>
          <MaskedLine delay={0.7}>
            Begins With <span className="italic text-bronze">Exceptional</span>
          </MaskedLine>
          <MaskedLine delay={0.85}>People.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.15 }}
          className="mt-8 max-w-xl text-ivory/70 text-sm md:text-base leading-relaxed font-light"
          data-testid="hero-subcopy"
        >
          We connect outstanding hospitality brands with exceptional leaders, culinary talent and
          service professionals while providing strategic advisory designed to elevate operations,
          guest experience and commercial performance.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-build-team"
            onClick={() => scrollToId("#contact")}
            className="text-[11px] tracking-[0.25em] uppercase px-8 py-4 bg-bronze text-ink hover:bg-ivory transition-colors duration-500"
          >
            Build Your Team
          </button>
          <button
            data-testid="hero-cta-explore"
            onClick={() => scrollToId("#about")}
            className="text-[11px] tracking-[0.25em] uppercase px-8 py-4 border border-ivory/40 text-ivory hover:bg-ivory hover:text-ink transition-all duration-500"
          >
            Explore Our Expertise
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.6 }}
          className="mt-14 flex items-center justify-between border-t border-ivory/15 pt-6"
        >
          <span className="text-[10px] tracking-[0.35em] uppercase text-ivory/50" data-testid="hero-regions">
            Europe &nbsp;·&nbsp; Middle East &nbsp;·&nbsp; Asia
          </span>
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            className="text-[10px] tracking-[0.35em] uppercase text-ivory/50"
          >
            Scroll
          </motion.span>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
